
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSettings } from '@/hooks/useAppSettings';
import { Loader } from 'lucide-react';

interface AppLoaderProps {
  fullScreen?: boolean;
  size?: 'small' | 'medium' | 'large';
  message?: string;
}

const AppLoader: React.FC<AppLoaderProps> = ({ 
  fullScreen = true, 
  size = 'medium', 
  message 
}) => {
  const { t } = useTranslation(['common']);
  const { appName, logoUrl } = useAppSettings();
  
  // Size styling
  const sizeClasses = {
    small: 'h-8 w-8',
    medium: 'h-12 w-12',
    large: 'h-16 w-16'
  };
  
  const logoSize = sizeClasses[size];
  
  // If fullScreen, show the loader in the center of the screen
  if (fullScreen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F7F5]">
        <div className="flex flex-col items-center animate-pulse">
          {logoUrl ? (
            <img src={logoUrl} alt={appName} className={`${logoSize} mb-4 object-contain`} />
          ) : (
            <div className={`${logoSize} mb-4 flex items-center justify-center rounded-full bg-[#E4002B]/10`}>
              <Loader className="h-2/3 w-2/3 text-[#E4002B] animate-spin" />
            </div>
          )}
          <span className="text-lg font-semibold text-[#E4002B]">
            {message || t('common:loading')}
          </span>
        </div>
      </div>
    );
  }
  
  // Non-fullscreen version for inline loading
  return (
    <div className="flex items-center justify-center py-8">
      <div className="flex flex-col items-center animate-pulse">
        {logoUrl ? (
          <img src={logoUrl} alt={appName} className={`${logoSize} mb-2 object-contain`} />
        ) : (
          <div className={`${logoSize} mb-2 flex items-center justify-center rounded-full bg-[#E4002B]/10`}>
            <Loader className="h-2/3 w-2/3 text-[#E4002B] animate-spin" />
          </div>
        )}
        {message && <span className="text-sm font-medium text-[#E4002B]">{message}</span>}
      </div>
    </div>
  );
};

export default AppLoader;
